/**
 * msdf-atlas-gen metrics (the font importer's produced JSON, cached at VFS_PATHS.cache(guid,'json'))
 * parsed into a fast glyph lookup. All font-space values are in EM units (multiply by fontSize);
 * atlasBounds are atlas PIXELS (yOrigin bottom by default).
 */
import type { FontStyleValue } from '../types';

export interface MsdfBounds {
  left: number;
  bottom: number;
  right: number;
  top: number;
}

export interface MsdfGlyph {
  advance: number;
  /** Glyph rect in em, relative to baseline (absent for whitespace). */
  plane?: MsdfBounds;
  /** Atlas rect in pixels (absent for whitespace). */
  atlas?: MsdfBounds;
}

interface MetricsJson {
  atlas?: { distanceRange?: number; size?: number; width?: number; height?: number; yOrigin?: string };
  metrics?: { lineHeight?: number; ascender?: number; descender?: number };
  glyphs?: {
    unicode: number;
    advance: number;
    planeBounds?: MsdfBounds;
    atlasBounds?: MsdfBounds;
  }[];
  kerning?: { unicode1: number; unicode2: number; advance: number }[];
}

export class MsdfFontData {
  private readonly glyphs = new Map<number, MsdfGlyph>();
  private readonly kern = new Map<number, number>();

  private constructor(
    /** Distance-field range in atlas texels (shader pxRange). */
    readonly distanceRange: number,
    /** Atlas font size (glyph px at emSize) — informational. */
    readonly atlasFontSize: number,
    readonly atlasWidth: number,
    readonly atlasHeight: number,
    readonly yOriginBottom: boolean,
    /** Default line height in em. */
    readonly lineHeight: number,
    /** Baseline offset from the line top, in em. */
    readonly ascender: number,
    readonly descender: number,
  ) {}

  static parse(json: unknown): MsdfFontData {
    const data = json as MetricsJson;
    // msdf-atlas-gen `-yorigin top` (our bake) emits a Y-DOWN metrics convention: ascender NEGATIVE,
    // descender positive, planeBounds `top` negative (above baseline) / `bottom` positive. layoutText
    // expects the standard Y-UP convention (ascender positive, plane.top above baseline positive), so
    // normalize here: flip the sign of ascender/descender and every glyph's plane top/bottom. Without
    // this, yTop/height are wrong per-glyph → glyphs scatter vertically. (`-yorigin bottom` is already Y-up.)
    const yDown = (data.atlas?.yOrigin ?? 'bottom') !== 'bottom';
    const sgn = yDown ? -1 : 1;
    const rawAsc = data.metrics?.ascender;
    const rawDesc = data.metrics?.descender;
    const font = new MsdfFontData(
      data.atlas?.distanceRange ?? 4,
      data.atlas?.size ?? 48,
      data.atlas?.width ?? 1,
      data.atlas?.height ?? 1,
      !yDown, // yOriginBottom (drives the atlas-UV orientation in layoutText)
      data.metrics?.lineHeight ?? 1.2,
      rawAsc != null ? sgn * rawAsc : 0.9,
      rawDesc != null ? sgn * rawDesc : -0.25,
    );
    for (const g of data.glyphs ?? []) {
      const pb = g.planeBounds;
      const plane =
        pb && yDown ? { left: pb.left, right: pb.right, top: -pb.top, bottom: -pb.bottom } : pb;
      font.glyphs.set(g.unicode, { advance: g.advance, plane, atlas: g.atlasBounds });
    }
    for (const k of data.kerning ?? []) {
      font.kern.set(k.unicode1 * 0x110000 + k.unicode2, k.advance);
    }
    return font;
  }

  glyph(codepoint: number): MsdfGlyph | undefined {
    return this.glyphs.get(codepoint);
  }

  kerning(a: number, b: number): number {
    return this.kern.get(a * 0x110000 + b) ?? 0;
  }
}

// ---------------------------------------------------------------------------
// Font registry (runtime, player-safe). The host (editor/player subsystem) loads the metrics JSON
// for every font key the mounted styles reference and registers it here; text layout reads it. A key
// is a font-asset guid, or a synthetic `<guid>#<instance>` for a per-weight variable-font instance.

/** One face registered under a family (W-TEXT): drives CSS font-family/weight/style resolution. */
export interface FontFace {
  /** Registry key (guid or `<guid>#<instance>`) — resolves to its MsdfFontData + atlas. */
  key: string;
  /** Typographic family name (matched case-insensitively). */
  family: string;
  weight: number; // 100..900
  width: number; // % (100 = normal)
  italic: boolean;
}

export interface FontRegisterOptions {
  asDefault?: boolean;
  /** Family metadata (from the importer's hb-info face) → makes the font resolvable by family. */
  face?: { family: string; weight?: number; width?: number; italic?: boolean };
}

const fonts = new Map<string, MsdfFontData>();
const familyIndex = new Map<string, FontFace[]>(); // lowercased family → faces
let defaultFontGuid: string | null = null;

function dropFace(key: string): void {
  for (const [fam, list] of familyIndex) {
    const next = list.filter((f) => f.key !== key);
    if (next.length !== list.length) {
      if (next.length) familyIndex.set(fam, next);
      else familyIndex.delete(fam);
    }
  }
}

/** Register a font under `key`. `opts` may be the legacy `asDefault` boolean or a {asDefault, face}. */
export function registerUIFont(
  key: string,
  font: MsdfFontData,
  opts: FontRegisterOptions | boolean = {},
): void {
  const o = typeof opts === 'boolean' ? { asDefault: opts } : opts;
  fonts.set(key, font);
  if (o.asDefault || defaultFontGuid === null) defaultFontGuid = key;
  dropFace(key); // idempotent on re-register (reimport)
  if (o.face?.family) {
    const face: FontFace = {
      key,
      family: o.face.family,
      weight: o.face.weight ?? 400,
      width: o.face.width ?? 100,
      italic: o.face.italic ?? false,
    };
    const fam = face.family.toLowerCase();
    const bucket = familyIndex.get(fam);
    if (bucket) bucket.push(face);
    else familyIndex.set(fam, [face]);
  }
}

export function getUIFont(key: string): MsdfFontData | null {
  return fonts.get(key) ?? null;
}

/** Font for a style's guid, falling back to the default font ('' or unknown guid). */
export function resolveUIFont(guid: string): { guid: string; font: MsdfFontData } | null {
  const own = fonts.get(guid);
  if (own) return { guid, font: own };
  if (defaultFontGuid !== null) {
    const def = fonts.get(defaultFontGuid);
    if (def) return { guid: defaultFontGuid, font: def };
  }
  return null;
}

/**
 * CSS Fonts weight-matching within a set of same-style candidates: exact, else the spec's directional
 * nearest (400–500 prefer heavier-then-lighter; <400 lighter-first; >500 heavier-first).
 */
export function pickFaceByWeight(faces: readonly FontFace[], want: number): FontFace {
  const exact = faces.find((f) => f.weight === want);
  if (exact) return exact;
  const nearest = (pred: (w: number) => boolean, dir: 'asc' | 'desc'): FontFace | undefined =>
    faces
      .filter((f) => pred(f.weight))
      .sort((a, b) => (dir === 'asc' ? a.weight - b.weight : b.weight - a.weight))[0];
  if (want >= 400 && want <= 500) {
    return (
      nearest((w) => w > want && w <= 500, 'asc') ??
      nearest((w) => w < want, 'desc') ??
      nearest((w) => w > 500, 'asc') ??
      faces[0]!
    );
  }
  if (want < 400) {
    return nearest((w) => w < want, 'desc') ?? nearest((w) => w > want, 'asc') ?? faces[0]!;
  }
  return nearest((w) => w > want, 'asc') ?? nearest((w) => w < want, 'desc') ?? faces[0]!;
}

/**
 * Resolve a `font-family` stack + weight/style to a concrete face. Walks the stack; the first family
 * with any registered face wins (style match preferred, then weight). null → caller uses the default.
 */
export function resolveUIFontFamily(
  families: readonly string[],
  weight = 400,
  italic = false,
): { guid: string; font: MsdfFontData } | null {
  for (const name of families) {
    const cands = familyIndex.get(name.toLowerCase());
    if (!cands || !cands.length) continue;
    const styled = cands.filter((f) => f.italic === italic);
    const face = pickFaceByWeight(styled.length ? styled : cands, weight);
    const font = fonts.get(face.key);
    if (font) return { guid: face.key, font };
  }
  return null;
}

/**
 * Resolve a text style's font: an explicit `font` guid wins (direct face override), else the
 * `fontFamily` stack is matched by weight/style, else the default font. The single seam both the
 * measurer and the paint walk call — keeps layout and paint on the same face (dual-call invariant).
 */
export function resolveTextFont(style: {
  font?: string;
  fontFamily?: readonly string[] | null;
  fontWeight?: number;
  fontStyle?: FontStyleValue;
}): { guid: string; font: MsdfFontData } | null {
  if (style.font) {
    const own = fonts.get(style.font);
    if (own) return { guid: style.font, font: own };
  }
  if (style.fontFamily && style.fontFamily.length) {
    const r = resolveUIFontFamily(style.fontFamily, style.fontWeight ?? 400, style.fontStyle === 'italic');
    if (r) return r;
  }
  if (defaultFontGuid !== null) {
    const def = fonts.get(defaultFontGuid);
    if (def) return { guid: defaultFontGuid, font: def };
  }
  return null;
}

export function clearUIFonts(): void {
  fonts.clear();
  familyIndex.clear();
  defaultFontGuid = null;
}
